import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { qk } from '@/lib/query-client'
import { useWorkspace } from '@/providers/workspace-provider'
import { useAuth } from '@/providers/auth-provider'
import type { Tables, TablesInsert, TablesUpdate } from '@/types/database'

export function useEvents() {
  const { workspaceId } = useWorkspace()
  return useQuery({
    queryKey: qk.events(workspaceId ?? 'none'),
    enabled: !!workspaceId,
    queryFn: async (): Promise<Tables<'events'>[]> => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('workspace_id', workspaceId!)
        .is('deleted_at', null)
        .order('start_at', { ascending: true, nullsFirst: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useUpsertEvent() {
  const { workspaceId } = useWorkspace()
  const { user } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      values,
    }: {
      id?: string
      values: Omit<TablesInsert<'events'>, 'workspace_id'>
    }) => {
      if (!workspaceId) throw new Error('Nenhum workspace selecionado')
      if (id) {
        const { error } = await supabase
          .from('events')
          .update(values as TablesUpdate<'events'>)
          .eq('id', id)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('events')
          .insert({ ...values, workspace_id: workspaceId, created_by: user?.id ?? null })
        if (error) throw error
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.events(workspaceId ?? 'none') }),
  })
}
